import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/common/Seo";
import VoicePlayer from "@/components/common/VoicePlayer";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import { CategoryTag } from "@/components/common/Badges";
import { SURVEY_QUESTIONS } from "@/lib/survey";

const TYPE_LABELS = {
  recipient: "Recipient", living_donor: "Living donor", family_member: "Family member",
  caregiver: "Caregiver", evaluation_patient: "Evaluation", other: "Contributor",
};
const ORGAN_LABELS = { kidney: "Kidney", liver: "Liver", heart: "Heart", lung: "Lung", pancreas: "Pancreas", intestine: "Intestine" };

export default function StoryPage() {
  const { id } = useParams();
  const [j, setJ] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setJ(null); setErr(false);
    api.get(`/journeys/${id}`).then((r) => setJ(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="max-w-3xl mx-auto px-6 py-24">Story not found or not yet published.</div>;
  if (!j) return <div className="max-w-3xl mx-auto px-6 py-24 text-neutral-500">Loading…</div>;

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <Seo title={`${j.public_label} — ${ORGAN_LABELS[j.organ]} journey`} description={`A ${ORGAN_LABELS[j.organ]} transplant journey from ${j.state_name || "India"}.`} />

      <div className="text-[11px] font-mono uppercase tracking-widest text-rust mb-4">
        {ORGAN_LABELS[j.organ]} transplant · {TYPE_LABELS[j.journey_type]}
      </div>
      <h1 className="text-3xl sm:text-5xl font-light tracking-tight text-ink">{j.public_label}</h1>
      <div className="flex flex-wrap items-center gap-3 mt-4 text-sm font-mono text-neutral-500">
        <span>{ORGAN_LABELS[j.organ]}</span>
        {j.state_name && <><span>·</span>{j.state_name}</>}
        {j.journey_year && <><span>·</span>{j.journey_year}</>}
        {j.hospital_name && (
          <>
            <span>·</span>
            {j.hospital_slug ? (
              <Link to={`/hospital/${j.hospital_slug}`} className="text-rust hover:text-rust-hover underline-offset-2 hover:underline transition-colors">{j.hospital_name}</Link>
            ) : (
              <span>{j.hospital_name}</span>
            )}
          </>
        )}
      </div>

      {j.has_audio && j.audio_url && (
        <section className="mt-12">
          <h2 className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-4">Listen to this journey</h2>
          <VoicePlayer src={j.audio_url} duration={j.audio_duration} />
          <p className="text-xs text-neutral-400 mt-3">This is the original human voice. We never replace it with a synthetic voice.</p>
        </section>
      )}

      {(j.story_text || j.transcript) && (
        <section className="mt-12">
          <h2 className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-4">
            {j.story_text ? "Read the story" : "Transcript"}
          </h2>
          <div className="prose prose-neutral max-w-none text-lg text-neutral-800 leading-relaxed whitespace-pre-wrap">
            {j.story_text || j.transcript}
          </div>
        </section>
      )}

      {j.lessons && (
        <section className="mt-12 bg-[#FAF8F5] border border-stone-200/80 p-7">
          <h2 className="text-[11px] font-mono uppercase tracking-widest text-rust mb-4">What I want other families to know</h2>
          <p className="text-lg text-neutral-800 leading-relaxed whitespace-pre-wrap">{j.lessons}</p>
        </section>
      )}

      {j.survey_highlights && (j.survey_highlights.why_hospital || j.survey_highlights.most_helpful) && (
        <section className="mt-12 grid sm:grid-cols-2 gap-4" data-testid="story-survey-highlights">
          {["why_hospital", "most_helpful"].map((k) => {
            const v = j.survey_highlights[k];
            if (!v || (Array.isArray(v) && v.length === 0)) return null;
            const q = SURVEY_QUESTIONS.find((s) => s.key === k)?.q;
            return (
              <div key={k} data-testid={`story-highlight-${k}`} className="rounded-2xl bg-white border border-neutral-200 p-6">
                <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-3">{q}</div>
                <div className="text-base text-ink font-medium leading-relaxed">{(Array.isArray(v) ? v : [v]).join(" · ")}</div>
              </div>
            );
          })}
        </section>
      )}

      {j.images && j.images.length > 0 && (
        <section className="mt-12">
          <h2 className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-4">Photos</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {j.images.map((src, i) => (
              <img key={i} src={src} alt={`Journey photo ${i + 1}`} loading="lazy"
                className="w-full aspect-square object-cover border border-neutral-200" />
            ))}
          </div>
        </section>
      )}

      {j.hospital_response && (
        <section className="mt-12 bg-neutral-100/60 border-l-2 border-l-ink border-t border-r border-b border-neutral-200 pl-5 pr-4 py-5">
          <CategoryTag kind="hospital" />
          <p className="text-neutral-800 leading-relaxed mt-4 whitespace-pre-wrap">{j.hospital_response}</p>
        </section>
      )}

      <StoryFeedback id={j.id} initialUp={j.feedback_up || 0} initialDown={j.feedback_down || 0} />

      <p className="text-xs text-neutral-400 mt-12 leading-relaxed">
        This is a personal experience shared anonymously. It is not medical advice, not a clinical assessment,
        and not a judgement of any hospital or individual.
      </p>
    </article>
  );
}

function StoryFeedback({ id, initialUp, initialDown }) {
  const [up, setUp] = useState(initialUp);
  const [down, setDown] = useState(initialDown);
  const [voted, setVoted] = useState(() => localStorage.getItem(`ts_vote_${id}`));

  useEffect(() => { setUp(initialUp); setDown(initialDown); }, [initialUp, initialDown]);

  const vote = async (reaction) => {
    if (voted) return;
    let voter = localStorage.getItem("ts_voter_id");
    if (!voter) { voter = crypto.randomUUID(); localStorage.setItem("ts_voter_id", voter); }
    try {
      const { data } = await api.post(`/journeys/${id}/react`, { reaction, voter_id: voter });
      setUp(data.up); setDown(data.down); setVoted(data.voted);
      localStorage.setItem(`ts_vote_${id}`, data.voted);
      toast.success("Thanks — your feedback was recorded.");
    } catch { /* stay quiet */ }
  };

  const btn = (active) =>
    `inline-flex items-center gap-2 px-4 py-2 text-sm border rounded-full transition-colors disabled:cursor-default ${
      active ? "border-ink bg-ink text-white" : "border-neutral-300 text-neutral-600 hover:border-ink"}`;

  return (
    <div data-testid="story-feedback" className="mt-14 pt-8 border-t border-neutral-200 flex flex-wrap items-center gap-3">
      <span className="text-[11px] font-mono uppercase tracking-widest text-neutral-400 mr-1">Was this story helpful?</span>
      <button data-testid="feedback-up" onClick={() => vote("up")} disabled={!!voted} className={btn(voted === "up")}>
        <ThumbsUp className="w-4 h-4" /> {up}
      </button>
      <button data-testid="feedback-down" onClick={() => vote("down")} disabled={!!voted} className={btn(voted === "down")}>
        <ThumbsDown className="w-4 h-4" /> {down}
      </button>
      {voted && <span className="text-xs text-neutral-400">Recorded anonymously.</span>}
    </div>
  );
}
